// Pure run-state model for the engagement cockpit (WORKSPACE_COCKPIT_SPEC.md
// §9.5). The backend owns run state; this reducer renders transitions locally
// so every §9.5 rule is unit-tested. No component holds this logic.

import type { RunEvent, RunStep, RunStatus, RunView } from './types.ts';

// ---- Run state reducer (§9.5) ----

export interface RunState {
  status: RunStatus;
  attempt: number;
  stale: boolean;
  /** Last confirmed update time (ISO); unchanged when going stale. */
  observedAt: string;
  stopRequestedAt: string | null;
  stopRequestedBy: string | null;
  stoppedAt: string | null;
  /** Attempts retained so the earlier attempt stays visible after a retry. */
  attempts: { attempt: number; status: RunStatus }[];
}

type RunViewFacts = Pick<RunView, 'status' | 'attempt' | 'stale' | 'observedAt'> &
  Partial<Pick<RunView, 'stopRequestedAt' | 'stopRequestedBy' | 'stoppedAt'>>;

export type RunAction =
  | { type: 'run-view'; view: RunViewFacts }
  | { type: 'stop-requested'; at: string; by: string }
  /** The stop request itself failed: nothing was requested, Stop is available again. */
  | { type: 'stop-failed' }
  | { type: 'stale' };

export const initialRunState = (view?: RunViewFacts): RunState => ({
  status: view?.status ?? 'not-started',
  attempt: view?.attempt ?? 1,
  stale: view?.stale ?? false,
  observedAt: view?.observedAt ?? '',
  stopRequestedAt: view?.stopRequestedAt ?? null,
  stopRequestedBy: view?.stopRequestedBy ?? null,
  stoppedAt: view?.stoppedAt ?? null,
  attempts: view ? [{ attempt: view.attempt, status: view.status }] : [],
});

/**
 * §9.5 transitions. Duplicate Run while running (or stop requested) and
 * duplicate Stop are no-ops; a stop acknowledgement only lands as `stopped`
 * when the backend confirms; `stale` never changes the status. (Start over
 * clears the store, not this reducer: the run is not stopped, records stay.)
 */
export function runReducer(state: RunState, action: RunAction): RunState {
  switch (action.type) {
    case 'run-view': {
      const v = action.view;
      const sameAttempt = state.attempt === v.attempt;
      // A delayed acknowledgement: the backend may still say "running" after our
      // request; only a different status from the backend replaces the request.
      const keepRequest =
        state.status === 'stop-requested' && v.status === 'running' && sameAttempt;
      const status = keepRequest ? 'stop-requested' : v.status;
      const requested = status === 'stop-requested';
      const same = state.status === status && sameAttempt;
      return {
        ...state,
        status,
        attempt: v.attempt,
        // The backend's stale flag is honoured; a local poll failure sets it
        // separately and a successful poll clears it again.
        stale: v.stale,
        observedAt: v.observedAt,
        // The request marker lives only while the request is outstanding; any
        // other backend status (stopped, completed, failed, ...) clears it.
        stopRequestedAt: requested ? (v.stopRequestedAt ?? state.stopRequestedAt) : null,
        stopRequestedBy: requested ? (v.stopRequestedBy ?? state.stopRequestedBy) : null,
        stoppedAt: status === 'stopped' ? (v.stoppedAt ?? state.stoppedAt ?? v.observedAt) : null,
        attempts: same
          ? state.attempts
          : [
              ...state.attempts.filter((a) => a.attempt !== v.attempt),
              { attempt: v.attempt, status },
            ],
      };
    }
    case 'stop-requested': {
      if (state.status !== 'running') return state; // repeats are no-ops
      return {
        ...state,
        status: 'stop-requested',
        stopRequestedAt: action.at,
        stopRequestedBy: action.by,
        // observedAt stays the last backend-confirmed time; a local request is not one.
      };
    }
    case 'stop-failed': {
      if (state.status !== 'stop-requested') return state;
      return { ...state, status: 'running', stopRequestedAt: null, stopRequestedBy: null };
    }
    case 'stale':
      return state.stale ? state : { ...state, stale: true };
  }
}

// ---- Terminal event view model (§4.2) ----

/** Glyph is derived from kind (and actorKind), never sent on the wire. */
export function glyphFor(event: Pick<RunEvent, 'kind' | 'actorKind'>): string {
  switch (event.kind) {
    case 'request':
      return '›';
    case 'answer':
      return '‹';
    case 'result':
      return '✓';
    case 'note':
      return '·';
    case 'attention':
      return '!';
    case 'failure':
      return '✕';
    case 'stopped':
      return '■';
    case 'decision':
      return event.actorKind === 'HUMAN' ? '✓' : '·';
  }
}

export const GLYPH_NAMES: Record<string, string> = {
  '›': 'Query',
  '‹': 'Answer',
  '✓': 'Result',
  '·': 'Note',
  '!': 'Attention',
  '✕': 'Failure',
  '■': 'Stopped',
};

export type TerminalFilter = 'activity' | 'requests' | 'decisions';

/** One event history is filtered, never forked (§4.3). */
export function eventMatchesFilter(event: Pick<RunEvent, 'kind'>, filter: TerminalFilter): boolean {
  switch (filter) {
    case 'activity':
      return true;
    case 'requests':
      return event.kind === 'request' || event.kind === 'answer';
    case 'decisions':
      return event.kind === 'decision' || event.kind === 'attention';
  }
}

export function filterEvents(events: readonly RunEvent[], filter: TerminalFilter): RunEvent[] {
  return events.filter((e) => eventMatchesFilter(e, filter));
}

/** "X of Y": X from the filtered retained events, Y from the backend count. */
export function showingCount(shown: number, total: number): string {
  return `Showing ${shown} of ${total} events`;
}

// ---- Bounded rendering (§4.5) ----

export const MAX_RENDERED_EVENTS = 500;
/** Page size for Load earlier events. */
export const EVENTS_PAGE_SIZE = 200;

/** Keeps at most MAX_RENDERED_EVENTS rows, dropping the oldest first. */
export function boundEvents(events: readonly RunEvent[]): { events: RunEvent[]; dropped: number } {
  if (events.length <= MAX_RENDERED_EVENTS) return { events: [...events], dropped: 0 };
  return {
    events: events.slice(events.length - MAX_RENDERED_EVENTS),
    dropped: events.length - MAX_RENDERED_EVENTS,
  };
}

// ---- Current work (§9.1) ----

export function stepCounts(steps: readonly RunStep[]) {
  const by = (s: RunStep['state']) => steps.filter((st) => st.state === s).length;
  const finished = by('completed');
  const skipped = by('skipped');
  return {
    total: steps.length,
    finished,
    skipped,
    failed: by('failed'),
    blocked: by('blocked'),
    running: by('running'),
    notStarted: by('not-started'),
    // A skipped step is labelled separately, never counted as tested coverage.
    sentence: steps.length ? `${finished} of ${steps.length} steps finished` : '',
  };
}

export function currentStep(steps: readonly RunStep[]): RunStep | null {
  return (
    steps.find((s) => s.state === 'running') ?? steps.find((s) => s.state === 'not-started') ?? null
  );
}

export function finishedSentence(
  status: RunStatus,
  counts: ReturnType<typeof stepCounts>,
): string | null {
  if (status === 'stopped') return `Run stopped. ${counts.sentence}.`;
  if (status === 'completed') return `Run completed. ${counts.sentence}.`;
  if (status === 'partially-complete')
    return `Run partially complete. ${counts.sentence}; ${counts.failed} failed, ${counts.skipped} skipped, ${counts.blocked} blocked.`;
  return null;
}

// ---- Run state badge (§9.2; text + shape, never colour alone) ----

export const RUN_STATUS_LABEL: Record<RunStatus, string> = {
  'not-started': 'Not started',
  running: 'Running',
  'stop-requested': 'Stop requested',
  stopped: 'Stopped',
  completed: 'Completed',
  'partially-complete': 'Partially complete',
  failed: 'Failed',
};

export function runStatusShape(status: RunStatus): string {
  switch (status) {
    case 'running':
      return '▶';
    case 'stop-requested':
      return '❙❙';
    case 'stopped':
      return '■';
    case 'completed':
      return '●';
    case 'partially-complete':
      return '◐';
    case 'failed':
      return '✕';
    case 'not-started':
      return '○';
  }
}

export function runStatusTone(
  status: RunStatus,
): 'neutral' | 'attention' | 'negative' | 'positive' {
  switch (status) {
    case 'failed':
      return 'negative';
    case 'stop-requested':
      return 'attention';
    case 'partially-complete':
      return 'attention';
    // Completed means the named activity finished, with review still explicit.
    case 'completed':
      return 'neutral';
    default:
      return 'neutral';
  }
}

// ---- Workspace command parser (§9.3; a closed table) ----

export type CommandAction =
  | { kind: 'scope' }
  | { kind: 'plan' }
  | { kind: 'why'; stepId: string }
  | { kind: 'evidence'; evidenceId: string }
  | { kind: 'context' }
  | { kind: 'remember'; text: string }
  | { kind: 'report' }
  | { kind: 'stop' }
  | { kind: 'help' }
  | { kind: 'unknown'; input: string }
  | { kind: 'free-text' };

export const COMMAND_NAMES = [
  'scope',
  'plan',
  'why',
  'evidence',
  'context',
  'remember',
  'report',
  'stop',
  'help',
] as const;

const COMMANDS: Record<(typeof COMMAND_NAMES)[number], { usage: string; scope: string }> = {
  scope: {
    usage: '/scope',
    scope: 'Read-only summary of the current run scope: targets, environment, exclusions.',
  },
  plan: {
    usage: '/plan',
    scope: 'Read-only summary of the selected plan revision. No comparison or editor.',
  },
  why: {
    usage: '/why CHK-03',
    scope: 'Explains the named check: why it runs, what it can establish, what it misses.',
  },
  evidence: {
    usage: '/evidence EV-102',
    scope: 'Opens a retained evidence artifact in the inspector.',
  },
  context: {
    usage: '/context',
    scope: 'Switches to this engagement’s Context tab: source finding, objective and scope.',
  },
  remember: {
    usage: '/remember NOTE',
    scope: 'Saves a note to this engagement’s memory. It never contacts a target.',
  },
  report: { usage: '/report', scope: 'Switches to this engagement’s Report tab.' },
  stop: {
    usage: '/stop',
    scope: 'Requests stop for the running work, exactly like the Stop control.',
  },
  help: { usage: '/help', scope: 'Lists the supported commands and their scope.' },
};

/** "/scope /plan …": the closed command table, for hints and announcements. */
export const COMMAND_LIST = COMMAND_NAMES.map((n) => `/${n}`).join(' ');

export function commandHelp(): string {
  return Object.values(COMMANDS)
    .map((c) => `${c.usage} — ${c.scope}`)
    .join('\n');
}

export const FREE_TEXT_ANSWER =
  "Questions in free text aren't answered yet; Traceira has no model in this product.";

/**
 * Parses one line of terminal input against the closed command table. No input
 * widens scope, bypasses eligibility, runs shell code, installs a tool, or
 * contacts a target; the only request `/stop` can make is the run-stop POST.
 */
export function parseCommand(raw: string): CommandAction {
  const input = raw.trim();
  if (input === '') return { kind: 'free-text' };
  if (!input.startsWith('/')) return { kind: 'free-text' };
  const [name, ...args] = input.slice(1).split(/\s+/);
  const step = args[0] ?? '';
  switch (name) {
    case 'scope':
      return { kind: 'scope' };
    case 'plan':
      return { kind: 'plan' };
    case 'why':
      return step ? { kind: 'why', stepId: step } : { kind: 'unknown', input };
    case 'evidence':
      return step ? { kind: 'evidence', evidenceId: step } : { kind: 'unknown', input };
    case 'context':
      return { kind: 'context' };
    case 'remember': {
      const text = input.slice('/remember'.length).trim();
      return text ? { kind: 'remember', text } : { kind: 'unknown', input };
    }
    case 'report':
      return { kind: 'report' };
    case 'stop':
      return { kind: 'stop' };
    case 'help':
      return { kind: 'help' };
    default:
      return { kind: 'unknown', input };
  }
}

/** Suggestions filtered by typed text (§4.5 footer). */
export function commandSuggestions(typed: string): string[] {
  const q = typed.trim().replace(/^\//, '').toLowerCase();
  if (!q) return COMMAND_NAMES.map((n) => `/${n}`);
  return COMMAND_NAMES.filter((n) => n.startsWith(q)).map((n) => `/${n}`);
}

// ---- URL params (§4.8; plain strings, fallback with a reason) ----

export type CockpitTab = 'work' | 'steps' | 'context' | 'memory' | 'evidence' | 'report';
export type InspectorKind = 'check' | 'event' | 'finding' | 'evidence' | 'scope' | 'plan';

const TABS: CockpitTab[] = ['work', 'steps', 'context', 'memory', 'evidence', 'report'];
const INSPECTORS: InspectorKind[] = ['check', 'event', 'finding', 'evidence', 'scope', 'plan'];
const VIEWS: TerminalFilter[] = ['activity', 'requests', 'decisions'];

export const TAB_LABEL: Record<CockpitTab, string> = {
  work: 'Work',
  steps: 'Steps',
  context: 'Context',
  memory: 'Memory',
  evidence: 'Evidence',
  report: 'Report',
};

export const VIEW_LABEL: Record<TerminalFilter, string> = {
  activity: 'Activity',
  requests: 'Requests',
  decisions: 'Decisions',
};

/** Coerces numeric-looking values back to strings; unknown values fall back. */
export function asString(value: unknown): string | null {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return null;
}

export interface CockpitSearch {
  tab: CockpitTab;
  view: TerminalFilter;
  inspector: InspectorKind | null;
  id: string | null;
}

export function readCockpitSearch(
  search: Record<string, unknown>,
): CockpitSearch & { fallbackNote: string | null } {
  const rawTab = asString(search.tab);
  const rawView = asString(search.view);
  const rawInspector = asString(search.inspector);
  const rawId = asString(search.id);
  const notes: string[] = [];
  const tab = TABS.includes(rawTab as CockpitTab) ? (rawTab as CockpitTab) : 'work';
  if (rawTab !== null && tab !== rawTab) notes.push(`Unknown tab “${rawTab}”; showing Work.`);
  const view = VIEWS.includes(rawView as TerminalFilter) ? (rawView as TerminalFilter) : 'activity';
  if (rawView !== null && view !== rawView)
    notes.push(`Unknown view “${rawView}”; showing Activity.`);
  const inspector = INSPECTORS.includes(rawInspector as InspectorKind)
    ? (rawInspector as InspectorKind)
    : null;
  if (rawInspector !== null && inspector === null)
    notes.push(`Unknown inspector “${rawInspector}”; showing the tab without an inspector.`);
  const id = rawId;
  return {
    tab,
    view,
    inspector: inspector && id ? inspector : null,
    id,
    fallbackNote: notes.join(' ') || null,
  };
}

// ---- Timestamps ----

/** Renders an ISO timestamp as HH:MM:SS(.mmm) for the terminal time column. */
export function eventTime(at: string): string {
  const m = at.match(/T(\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?)/);
  return m?.[1] ?? at;
}

/** Short clock time for display ("14:06:32 UTC"); the full value goes in a title. */
export function clockTime(iso: string): string {
  const m = iso.match(/^\d{4}-\d{2}-\d{2}T(\d{2}:\d{2}:\d{2})(?:\.\d+)?Z$/);
  return m ? `${m[1]} UTC` : iso;
}

export const STEP_STATE_LABEL: Record<RunStep['state'], string> = {
  'not-started': 'Not started',
  running: 'Running',
  completed: 'Finished',
  failed: 'Failed',
  skipped: 'Skipped',
  blocked: 'Blocked',
};

export function durationLabel(ms: number): string {
  return ms >= 1000 ? `${(ms / 1000).toFixed(ms % 1000 === 0 ? 0 : 1)} s` : `${ms} ms`;
}
